import { useEffect, useState } from 'react';
import { Icon } from '@sakhtar/design-system/react';
import { apiGet } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type { PartySearchResponse, ProductSearchResponse } from '../../lib/types/topbar.js';

interface RecentEntry {
  readonly label: string;
  readonly kind: string;
}

function recentsKey(slug: string): string {
  return `sakhtar-topbar-recent-searches:${slug}`;
}

function readRecents(slug: string): readonly RecentEntry[] {
  try {
    const raw = window.localStorage.getItem(recentsKey(slug));
    return raw ? (JSON.parse(raw) as RecentEntry[]) : [];
  } catch {
    return [];
  }
}

function pushRecent(slug: string, entry: RecentEntry): void {
  try {
    const next = [entry, ...readRecents(slug).filter((r) => r.label !== entry.label)].slice(0, 5);
    window.localStorage.setItem(recentsKey(slug), JSON.stringify(next));
  } catch {
    // localStorage can throw (private browsing) — recents are a convenience, not required.
  }
}

/**
 * UI_REFERENCE_SOURCE: Sakhtar Topbar Popovers.dc.html §06 جستجوی سراسری.
 * Real, but narrower than the board: only GET .../master-data/parties and
 * .../master-data/products support `?search=` (apps/api/src/master-data/
 * party.controller.ts, product.controller.ts). No search endpoint exists
 * for orders, projects, or tasks — the board's "سفارش‌ها، پروژه‌ها، کارها"
 * groups are not built here rather than faked. Recent searches are a real
 * per-viewer localStorage convenience (this browser only), not fabricated
 * server data.
 */
export function SearchPopover({
  slug,
  q,
  onQ,
}: {
  slug: string;
  q: string;
  onQ: (v: string) => void;
}) {
  const base = tenantBasePath(slug);
  const [parties, setParties] = useState<PartySearchResponse | null>(null);
  const [products, setProducts] = useState<ProductSearchResponse | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [recents, setRecents] = useState<readonly RecentEntry[]>(() => readRecents(slug));

  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setStatus('idle');
      return;
    }
    setStatus('loading');
    const timer = setTimeout(() => {
      Promise.all([
        apiGet<PartySearchResponse>(
          `${base}/api/v1/master-data/parties?search=${encodeURIComponent(trimmed)}&limit=5`,
        ),
        apiGet<ProductSearchResponse>(
          `${base}/api/v1/master-data/products?search=${encodeURIComponent(trimmed)}&limit=5`,
        ),
      ])
        .then(([p, pr]) => {
          setParties(p);
          setProducts(pr);
          setStatus('ready');
        })
        .catch(() => setStatus('error'));
    }, 300);
    return () => clearTimeout(timer);
  }, [q, base]);

  function selectResult(label: string, kind: string) {
    pushRecent(slug, { label, kind });
    setRecents(readRecents(slug));
  }

  const trimmed = q.trim();
  const hasResults =
    status === 'ready' && ((parties?.items.length ?? 0) > 0 || (products?.items.length ?? 0) > 0);

  return (
    <div style={{ maxHeight: 440, overflowY: 'auto' }}>
      {trimmed.length === 0 ? (
        recents.length > 0 ? (
          <>
            <div
              style={{
                padding: '12px 14px 6px',
                fontSize: 11,
                fontWeight: 500,
                color: 'var(--sk-fg-subtle)',
                fontFamily: 'var(--sk-font-mono)',
                letterSpacing: '.08em',
              }}
            >
              جستجوهای اخیر
            </div>
            <div style={{ padding: '4px 6px 10px', display: 'flex', flexDirection: 'column' }}>
              {recents.map((r) => (
                <div
                  key={r.label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    height: 44,
                    padding: '0 10px',
                    borderRadius: 10,
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                  onClick={() => onQ(r.label)}
                >
                  <span style={{ color: 'var(--sk-fg-subtle)', display: 'flex' }}>
                    <Icon name="search" size="sm" />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>{r.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>{r.kind}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div
            style={{
              padding: '26px 16px',
              textAlign: 'center',
              fontSize: 12,
              color: 'var(--sk-fg-subtle)',
            }}
          >
            برای جست‌وجو حداقل ۲ نویسه وارد کنید.
          </div>
        )
      ) : null}

      {trimmed.length === 1 ? (
        <div
          style={{
            padding: '26px 16px',
            textAlign: 'center',
            fontSize: 12,
            color: 'var(--sk-fg-subtle)',
          }}
        >
          برای جست‌وجو حداقل ۲ نویسه وارد کنید.
        </div>
      ) : null}

      {status === 'loading' ? (
        <div
          style={{
            padding: '26px 16px',
            textAlign: 'center',
            fontSize: 12,
            color: 'var(--sk-fg-subtle)',
          }}
        >
          در حال جست‌وجو…
        </div>
      ) : null}

      {status === 'error' ? (
        <div style={{ padding: '20px 14px' }}>
          <div className="sk-alert sk-alert--danger">
            <Icon name="warn" size="md" />
            <div className="sk-alert__body">
              <div className="sk-alert__title">جست‌وجو ناموفق بود</div>
            </div>
          </div>
        </div>
      ) : null}

      {status === 'ready' ? (
        <>
          <div
            style={{
              padding: '12px 14px',
              borderBottom: '1px solid var(--sk-divider)',
              fontSize: 12,
              color: 'var(--sk-fg-muted)',
            }}
          >
            نتایج برای «<b style={{ color: 'var(--sk-fg)' }}>{q}</b>»
          </div>
          {!hasResults ? (
            <div
              style={{
                padding: '26px 16px',
                textAlign: 'center',
                fontSize: 12,
                color: 'var(--sk-fg-subtle)',
              }}
            >
              نتیجه‌ای یافت نشد.
            </div>
          ) : null}
          {(parties?.items.length ?? 0) > 0 ? (
            <div style={{ borderBottom: '1px solid var(--sk-divider)', padding: '8px 6px' }}>
              <div
                style={{
                  padding: '4px 10px 6px',
                  fontSize: 11,
                  fontWeight: 500,
                  color: 'var(--sk-fg-subtle)',
                  fontFamily: 'var(--sk-font-mono)',
                  letterSpacing: '.08em',
                }}
              >
                طرف‌حساب‌ها
              </div>
              {parties!.items.map((p) => (
                <a
                  key={p.partyId}
                  href={`${base}/master-data/parties`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    minHeight: 48,
                    padding: '6px 10px',
                    borderRadius: 10,
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                  onClick={() => selectResult(p.name, 'طرف‌حساب')}
                >
                  <span
                    style={{
                      width: 30,
                      height: 30,
                      flex: '0 0 auto',
                      borderRadius: 9,
                      background: 'var(--sk-bg-canvas)',
                      border: '1px solid var(--sk-border)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--sk-fg-muted)',
                    }}
                  >
                    <Icon name="user" size="sm" />
                  </span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</span>
                    <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>{p.code}</span>
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)', flex: '0 0 auto' }}>
                    {p.kind ?? ''}
                  </span>
                </a>
              ))}
            </div>
          ) : null}
          {(products?.items.length ?? 0) > 0 ? (
            <div style={{ padding: '8px 6px' }}>
              <div
                style={{
                  padding: '4px 10px 6px',
                  fontSize: 11,
                  fontWeight: 500,
                  color: 'var(--sk-fg-subtle)',
                  fontFamily: 'var(--sk-font-mono)',
                  letterSpacing: '.08em',
                }}
              >
                کالاها
              </div>
              {products!.items.map((p) => (
                <a
                  key={p.productId}
                  href={`${base}/master-data/products`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    minHeight: 48,
                    padding: '6px 10px',
                    borderRadius: 10,
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                  onClick={() => selectResult(p.name, 'کالا')}
                >
                  <span
                    style={{
                      width: 30,
                      height: 30,
                      flex: '0 0 auto',
                      borderRadius: 9,
                      background: 'var(--sk-bg-canvas)',
                      border: '1px solid var(--sk-border)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--sk-fg-muted)',
                    }}
                  >
                    <Icon name="cart" size="sm" />
                  </span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</span>
                    <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>{p.code}</span>
                  </span>
                </a>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
